import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PurchaseService } from '../services/purchase.service';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { catchError, take, takeUntil, tap } from 'rxjs/operators';
import { PaymentOptions } from './models/payment-options.model';
import { PathFinderService } from '../services/path-finder.service';
import { NotificationService } from '../services/notification.service';
import { EMPTY, Subject } from 'rxjs';
import { LoadingService } from '../services/loading.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { EventsService } from '../services/events.service';

@Component({
  selector: 'app-payment-options',
  templateUrl: './payment-options.component.html',
  styleUrls: ['./payment-options.component.scss']
})
export class PaymentOptionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() quoteStateId: number;
  @Input() questionForm: UntypedFormGroup;
  @Input() canLoadContent: boolean;

  loading: boolean;
  isDataReady: boolean;
  paymentOption: UntypedFormGroup;
  paymentCharge: UntypedFormGroup;
  paymentDueDate: UntypedFormGroup;
  valuationNextDayPaymentCharge: UntypedFormGroup;
  valuationFasterPaymentCharge: UntypedFormGroup;
  hasWalkIn3Exception: UntypedFormGroup;

  paymentOptionsArray: Array<PaymentOptions> = new Array<PaymentOptions>();
  fastPaymentCharge: number;
  nextDayPaymentCharge: number;
  premiumServicePaymentCharge: number;
  collectionServicePaymentCharge: number;

  isFastPaymentSelected = false;
  isNextDayPaymentSelected = false;
  isBacsPaymentSelected = false;
  isPremiumServicePaymentSelected = false;
  isCollectionServicePaymentSelected = false;
  isFastPaymentAvailable = false;
  isNextDayPaymentAvailable = false;
  isPremiumServicePaymentAvailable = false;
  isCollectionServicePaymentAvailable = false;
  destroy$ = new Subject();

  constructor(
    private purchaseService: PurchaseService,
    private pathFinderService: PathFinderService,
    private loadingService: LoadingService,
    private notifications: NotificationService,
    private modalService: NgbModal,
    private eventService: EventsService
  ) { }




  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.canLoadContent.firstChange && changes.canLoadContent.currentValue === undefined) || changes.canLoadContent.currentValue) {
      this.loadData(true);
    }
  }

  loadData(value: boolean) {
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    this.isDataReady = value;
    this.getPaymentMethodOptions();
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadingService.loading.emit(this.loading);
    this.getFasterPaymentBankCheck();
    this.createPaymentOptionsFormGroup();

    this.eventService.answersSaved.pipe(takeUntil(this.destroy$), take(1)).subscribe(value => {
      this.loadData(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  getPaymentMethodOptions() {
    if (this.isDataReady) {
      this.purchaseService.getPaymentOptions$(this.quoteStateId).pipe(
        tap(result => {
          if (result !== null) {
            result.paymentOptions.forEach(element => {
              const paymentOption = new PaymentOptions;
              paymentOption.paymentTypeId = element.paymentMethodTypeId;
              paymentOption.paymentName = element.paymentMethodTypeName;
              paymentOption.paymentCharge = element.paymentCharge;
              paymentOption.paymentDueDate = element.dueDate;
              this.paymentOptionsArray.push(paymentOption);
            });
          }

          const currentCustomerIdSelfieAnswer = this.getAnswerValue('CustomerIdSelfieRequired');
          if (currentCustomerIdSelfieAnswer == '0' || result.hasWalkin3Exception) {
            this.hasWalkIn3Exception.patchValue({ value: '0' })
          } else {
            this.hasWalkIn3Exception.patchValue({ value: '1' })
          }

          this.checkPaymentOptionsAvailability(this.paymentOptionsArray);
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
        }),
        catchError(() => {
          this.notifications.warningToast(`Unable to retrieve payment options from Finance. Defaulted to 4 working day payment.`);
          this.loading = false;
          this.loadingService.loading.emit(this.loading);
          const paymentOption = new PaymentOptions;
          paymentOption.paymentCharge = 0.0;
          paymentOption.paymentDueDate = this.addWorkDays(new Date(), 4);
          paymentOption.paymentName = 'BacsPayment';
          paymentOption.paymentTypeId = 3;
          this.paymentOptionsArray.push(paymentOption)
          this.selectBacsPayment();
          return EMPTY;
        }),
        take(1)
      ).subscribe();
    }
  }

  addWorkDays(startDate: Date, daysToAdd: number): Date {
    const dow = startDate.getDay();
    if (dow === 0) {
      daysToAdd++;
    }
    if (dow + daysToAdd >= 6) {
      const remainingWorkDays = daysToAdd - (5 - dow);
      daysToAdd += 2;
      if (remainingWorkDays > 5) {
        daysToAdd += 2 * Math.floor(remainingWorkDays / 5);
        if (remainingWorkDays % 5 === 0) {
          daysToAdd -= 2;
        }
      }
    }
    startDate.setDate(startDate.getDate() + daysToAdd);
    return startDate;
  }

  getFasterPaymentBankCheck() {
    return true;
  }

  setSelectedPaymentAnswers() {
    let typeName = '';
    let typeId: string;
    let dueDate: Date;
    let charge = 0.0;
    if (this.paymentOptionsArray.length > 0) {
      if (this.isFastPaymentSelected) {
        typeName = 'FastPayment';
      } else if (this.isNextDayPaymentSelected) {
        typeName = 'NextDayPayment';
      } else if (this.isPremiumServicePaymentSelected) {
        typeName = 'PremiumServicePayment';
      } else if (this.isCollectionServicePaymentSelected) {
        typeName = 'CollectionServicePayment';
      } else {
        typeName = 'BacsPayment';
      }
      const type = this.paymentOptionsArray.find(x => x.paymentName === typeName);
      typeId = type.paymentTypeId.toString();
      dueDate = type.paymentDueDate;
      charge = type.paymentCharge;
    } else {
      typeName = 'BacsPayment';
      typeId = '3';
      dueDate = this.addWorkDays(new Date(), 4);
    }
    this.paymentOption.patchValue({ value: typeId });
    this.paymentDueDate.patchValue({ value: dueDate });
    this.paymentCharge.patchValue({ value: charge });
  }

  checkPaymentOptionsAvailability(paymentOptions: Array<PaymentOptions>) {
    const isTrade = this.pathFinderService.isTradePurchase();
    const isCollection = this.pathFinderService.isCollection();

    paymentOptions.forEach(element => {
      if (element.paymentName.toLowerCase() === 'fastpayment') {
        if (this.getFasterPaymentBankCheck() && !isTrade) {
          this.isFastPaymentAvailable = true;
          this.fastPaymentCharge = element.paymentCharge;
          this.valuationFasterPaymentCharge.patchValue({ value: element.paymentCharge });
        }
      }
      if (element.paymentName.toLowerCase() === 'nextdaypayment' && !isTrade) {
        this.isNextDayPaymentAvailable = true;
        this.nextDayPaymentCharge = element.paymentCharge;
        this.valuationNextDayPaymentCharge.patchValue({ value: element.paymentCharge });
      }
      if (element.paymentName.toLowerCase() === 'premiumservicepayment' && !isTrade) {
        this.isPremiumServicePaymentAvailable = true;
        this.premiumServicePaymentCharge = element.paymentCharge;
      }
      if (element.paymentName.toLowerCase() === 'collectionservicepayment' && !isTrade && isCollection) {
        this.isCollectionServicePaymentAvailable = true;
        this.collectionServicePaymentCharge = element.paymentCharge;
      }
    });
    if (!this.isFastPaymentAvailable && !this.isNextDayPaymentAvailable) {
      this.selectBacsPayment();
    }
  }

  unselectAll() {
    this.isBacsPaymentSelected = false;
    this.isNextDayPaymentSelected = false;
    this.isFastPaymentSelected = false;
    this.isPremiumServicePaymentSelected = false;
    this.isCollectionServicePaymentSelected = false;
    this.pathFinderService.endTransitionEvent();
  }

  selectFastPayment() {
    this.unselectAll();
    this.isFastPaymentSelected = true;
    this.setFormStatus();
  }

  selectNextDayPayment() {
    this.unselectAll();
    this.isNextDayPaymentSelected = true;
    this.setFormStatus();
  }

  selectBacsPayment() {
    this.unselectAll();
    this.isBacsPaymentSelected = true;
    this.setFormStatus();
  }

  selectPremiumServicePayment() {
    this.unselectAll();
    this.isPremiumServicePaymentSelected = true;
    this.setFormStatus();
    this.registerConfirmationPopup('Premium Service', `<div><ul><li>We will book & pay for your taxi</li>
      <li>The journey must be less than 20 miles and ordered by 7pm</li>
      <li>You consent to us giving Uber your address for the purpose of booking your ride</li>
      <li>This option also includes immediate payment</li></ul></div>
      <div>Would you like to proceed with the premium service option?</div>`);
  }

  selectCollectionServicePayment() {
    this.unselectAll();
    this.isCollectionServicePaymentSelected = true;
    this.setFormStatus();
  }

  registerConfirmationPopup(title: string, message: string) {
    this.pathFinderService.registerTransitionEvent((onComplete => {
      const modalRef = this.modalService.open(ConfirmationComponent, {
        keyboard: false,
        backdrop: 'static',
        centered: true,
        size: 'm',
      });
      modalRef.componentInstance.title = title;
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.failButton = 'No';
      modalRef.componentInstance.successButton = 'Yes';

      modalRef.result.then(
        () => {
          onComplete(true);
        },
        () => {
          onComplete(false);
        }
      );
    }));
  }

  checkIfFormValid() {
    if (this.isNextDayPaymentSelected || this.isFastPaymentSelected || this.isBacsPaymentSelected
      || this.isPremiumServicePaymentSelected || this.isCollectionServicePaymentSelected) {
      return true;
    } else {
      return false;
    }
  }

  setFormStatus() {
    const isValid = this.checkIfFormValid();
    if (isValid) {
      this.setSelectedPaymentAnswers();
    } else {
      this.paymentOption.patchValue({ value: null });
      this.paymentCharge.patchValue({ value: null });
      this.paymentDueDate.patchValue({ value: null });
    }
  }

  createPaymentOptionsFormGroup() {
    if (this.questionForm) {
      const controlArray = this.questionForm.get('items') as UntypedFormArray;
      this.paymentOption = controlArray.controls[0] as UntypedFormGroup;
      this.paymentCharge = controlArray.controls[1] as UntypedFormGroup;
      this.paymentDueDate = controlArray.controls[2] as UntypedFormGroup;
      this.valuationFasterPaymentCharge = controlArray.controls[3] as UntypedFormGroup;
      this.valuationNextDayPaymentCharge = controlArray.controls[4] as UntypedFormGroup;
      this.hasWalkIn3Exception = controlArray.controls[5] as UntypedFormGroup;
    }
  }

  getAnswerValue(parameterName: string) {
    const answer = this.pathFinderService.getAnswerValue(parameterName, null);
    return answer;
  }
}
